import React, { FC } from 'react';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { getSurveyContentRequest, ISurveyContent } from '../../../services/surveyContent';
import { IScheduleSurvey } from '../../../services/surveys';
import { CustomDatePicker } from '../../../shared/DatePicker/DatePicker';
import ScheduleTimePicker from '../../../shared/ScheduleTimePicker/ScheduleTimePicker';
import Checkbox from '../../Checkbox/Checkbox';
import { ContentModal } from '../ContentModal';
import {
  Box,
  Text,
  Wrapper,
  DeleteReminder,
  OptionLabel,
  FormatOptions,
  FormatOption,
} from './ScheduleContent.styles';

interface ScheduleContentProps {
  title: string;
  schedule: IScheduleSurvey;
  externalSurveyId: string;
  isFormatOption?: boolean;
  isModalOptions?: boolean;
  isReminder?: boolean;
  isSms?: boolean;
  setIsSms?: () => void;
  onDelete?: () => void;
  updateSchedule?: (updates: Partial<IScheduleSurvey>) => void;
  dateKey: 'start_date' | 'end_date';
  notificationOptions: { label: string; disabled: boolean }[];
}

const ScheduleContent: FC<ScheduleContentProps> = ({
  title,
  schedule,
  externalSurveyId,
  isFormatOption = true,
  isModalOptions = true,
  isReminder,
  isSms = false,
  setIsSms,
  onDelete,
  updateSchedule,
  dateKey,
  notificationOptions,
}) => {
  const params = useParams();
  const surveyId = params.surveyId;
  const isDateSelected = schedule[dateKey] ? dayjs(schedule[dateKey]) : null;

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    if (date && updateSchedule) {
      const combinedDateTime = date.hour(19).minute(0).second(0);

      updateSchedule({ [dateKey]: combinedDateTime.toISOString() });
    }
  };

  const handleTimeChange = (time: dayjs.Dayjs | null) => {
    if (time && updateSchedule) {
      const currentDate = dayjs(schedule?.[dateKey] || dayjs());
      const updatedDate = currentDate.hour(time.hour()).minute(time.minute()).toISOString();
      updateSchedule({ [dateKey]: updatedDate });
    }
  };

  const getContent = async (id?: string) => {
    const content: ISurveyContent = await getSurveyContentRequest({ id });
    return content.invitation_email.translations;
  };

  const disabledDate = (current: dayjs.Dayjs) => {
    const today = dayjs().startOf('day');

    if (dateKey === 'start_date') {
      return current && current.isBefore(today, 'day');
    }

    if (dateKey === 'end_date') {
      const startDate = schedule.start_date ? dayjs(schedule.start_date).startOf('day') : today;
      return current && current.isBefore(startDate, 'day');
    }

    return current && current.isBefore(today, 'day');
  };

  return (
    <Wrapper isPast={false}>
      <Box>
        <Text>{title}</Text>
      </Box>
      <Box>
        <CustomDatePicker
          onChange={handleDateChange}
          defaultValue={schedule[dateKey] ? dayjs(schedule[dateKey]) : null}
          disabledDate={disabledDate}
        />
      </Box>
      <Box>
        <ScheduleTimePicker
          onChange={handleTimeChange}
          defaultValue={schedule[dateKey] ? dayjs(schedule[dateKey]) : null}
          selectedDate={schedule[dateKey] ? dayjs(schedule[dateKey]) : null}
          disabled={!isDateSelected}
        />
      </Box>
      <Box>
        {isFormatOption && (
          <FormatOptions>
            {notificationOptions.map(({ label, disabled }) => (
              <FormatOption key={label}>
                <Checkbox
                  checked={disabled ? true : isSms}
                  disabled={disabled}
                  onClick={() => {
                    if (!disabled && setIsSms && updateSchedule) {
                      setIsSms();
                      updateSchedule({ include_sms_invitation: !isSms });
                    }
                  }}
                />
                <OptionLabel>{label}</OptionLabel>
              </FormatOption>
            ))}
          </FormatOptions>
        )}
      </Box>
      <Box>
        {isModalOptions && (
          <ContentModal
            getContent={getContent}
            surveyId={surveyId}
            externalSurveyId={externalSurveyId}
          />
        )}
      </Box>
      {isReminder && onDelete && (
        <Box>
          <DeleteReminder data-testid="delete-reminder-button" onClick={onDelete} />
        </Box>
      )}
    </Wrapper>
  );
};

export default ScheduleContent;
